import * as React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Menu,
  MenuItem,
  Tooltip,
  FormControl,
  Select,
  Pagination,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useOrgan } from "../../hooks/useOrgan";
import { organizationPrimaryColors } from "../../layouts/PermanentSidebarMUI";
import { maskCurrency, maskInteger } from "../../utils/maskUtils";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import {
  getActionButtonStyles,
  getActionIconColor,
} from "../../utils/actionButtonStyles";
import TableSkeleton from "./TableSkeleton";

// Função auxiliar para obter valor de um objeto aninhado usando notação de ponto
function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
}

function Row({
  row,
  columns,
  collapseConfig,
  actions,
  showActions = true,
  defaultOpen = false,
  rowColorConfig,
}) {
  const theme = useTheme();

  const hasDataInCollapse = React.useMemo(() => {
    return (collapseConfig?.key || []).some((key) => (row[key] || []).length > 0);
  }, [row, collapseConfig?.key]);

  const showToggle =
    hasDataInCollapse || Boolean(collapseConfig?.showToggleWhenEmpty);

  const [open, setOpen] = React.useState(defaultOpen && hasDataInCollapse);

  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);

    if (nextOpen) {
      collapseConfig?.onExpand?.(row);
    }
  };

  // Estados do menu suspenso das ações
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action) => {
    handleCloseMenu();
    if (action?.onClick) action.onClick(row);
  };

  // Calcula os totais para os campos internos de cada linha
  const totals = React.useMemo(() => {
    const calculatedTotals = {};
    if (collapseConfig?.totalField) {
      collapseConfig.totalField.forEach((path) => {
        const [arrayKey, fieldKey] = path.split(".");
        const nestedArray = row[arrayKey] || [];
        calculatedTotals[path] = nestedArray.reduce(
          (acc, item) => acc + (Number(item[fieldKey]) || 0),
          0,
        );
      });
    }
    return calculatedTotals;
  }, [row, collapseConfig?.totalField]);

  const rowColors = React.useMemo(() => {
    // Mantém o padrão atual se não houver config
    if (!rowColorConfig) {
      return { backgroundColor: "#f5f5f5", color: "inherit" };
    }

    const { colorMap, propertyKey } = rowColorConfig;
    const propertyValue = getNestedValue(row, propertyKey);

    if (colorMap && propertyValue !== undefined) {
      const colorConfig = colorMap[propertyValue];
      if (colorConfig) {
        return {
          backgroundColor: colorConfig.bgColor,
          color: colorConfig.color,
        };
      }
    }

    return { backgroundColor: "#f5f5f5", color: "inherit" };
  }, [row, rowColorConfig]);

  // Filtrar ações visíveis ANTES de renderizar
  const visibleActions = React.useMemo(() => {
    return (
      actions?.filter((action) => {
        try {
          return action.show ? action.show(row) : true;
        } catch {
          return false;
        }
      }) || []
    );
  }, [actions, row]);

  // Se não houver ações visíveis, não mostrar o botão
  const hasVisibleActions = visibleActions.length > 0;

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: rowColors.backgroundColor,
          //! cor do espaço entre as linhas da tabela
          "& .MuiTableCell-root": {
            borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
          },
        }}>
        <TableCell>
          {showToggle && (
            <IconButton size="small" onClick={handleToggle}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>

        {columns.map((col) => {
          // Verifica se a coluna atual corresponde a um dos campos de totalização
          const isTotalField =
            collapseConfig.totalField &&
            collapseConfig.totalField.some((path) => path.endsWith(col.key));

          if (isTotalField) {
            // Se for um campo de totalização, busca o total correspondente
            const totalKey = collapseConfig.totalField.find((path) =>
              path.endsWith(col.key),
            );
            return (
              <TableCell
                key={col.key}
                align={col.align || "right"}
                sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                {col.isMoney
                  ? maskCurrency(totals[totalKey])
                  : col.isInteger
                    ? maskInteger(totals[totalKey])
                    : totals[totalKey]}
              </TableCell>
            );
          }

          // Se não for um campo de totalização, renderiza o valor normal
          return (
            <Tooltip
              title={row[col.key]}
              key={col.key}
              placement="bottom-start">
              <TableCell
                key={col.key}
                align={col.align || "left"}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 200,
                  color: rowColors.color,
                  textTransform: "uppercase",
                }}>
                {col.render
                  ? col.render(row[col.key], row)
                  : row[col.key] !== undefined && row[col.key] !== null
                    ? row[col.key]
                    : ""}
              </TableCell>
            </Tooltip>
          );
        })}

        {/* Menu suspenso de ações - Só mostra botão se houver ações visíveis */}
        {showActions && hasVisibleActions && (
          <TableCell align="center">
            <IconButton
              size="small"
              sx={{ paddingX: 2, color: rowColors.color }}
              onClick={handleOpenMenu}>
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}>
              {visibleActions.map((action, idx) => {
                const actionStyles = getActionButtonStyles(
                  action.buttonClass,
                  theme,
                );
                const iconColor = getActionIconColor(action.buttonClass, theme);

                return (
                  <MenuItem
                    key={idx}
                    onClick={() => handleActionClick(action)}
                    sx={{
                      // Aplica backgroundColor se existir, senão mantém transparente
                      ...(actionStyles.backgroundColor && {
                        backgroundColor: actionStyles.backgroundColor,
                        "&:hover": {
                          backgroundColor:
                            actionStyles["&:hover"]?.backgroundColor ||
                            actionStyles.backgroundColor,
                        },
                      }),
                      // Cor do texto
                      color: actionStyles.color,
                      // Hover effects padrões do MUI (opcional)
                      "&:hover": {
                        opacity: 0.9,
                      },
                    }}>
                    <FontAwesomeIcon
                      icon={action.icon}
                      style={{
                        marginRight: 8,
                        // Aplica cor no ícone se for customizada
                        color: iconColor,
                      }}
                    />
                    {action.title}
                  </MenuItem>
                );
              })}
            </Menu>
          </TableCell>
        )}

        {/* Célula vazia para manter alinhamento quando não há ações */}
        {showActions && !hasVisibleActions && <TableCell />}
      </TableRow>

      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={columns.length + 2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {collapseConfig.key.map((key, index) => {
                const subTitle = collapseConfig.titles?.[index];
                const subColumns = collapseConfig.columns?.[index];
                const subData = row[key] || [];

                if (subData.length === 0) {
                  if (!collapseConfig.renderEmpty) return null;

                  return (
                    <div key={key}>
                      {subTitle && (
                        <Typography variant="h6" gutterBottom>
                          {subTitle}
                        </Typography>
                      )}
                      {collapseConfig.renderEmpty(row)}
                    </div>
                  );
                }

                return (
                  <div key={key}>
                    <Typography variant="h6" gutterBottom>
                      {subTitle}
                    </Typography>
                    <Table size="small" aria-label={`Tabela de ${subTitle}`}>
                      <TableHead>
                        <TableRow>
                          {subColumns.map((col) => (
                            <TableCell
                              key={col.key}
                              align={col.align || "left"}
                              sx={{
                                fontWeight: "bold",
                                width: col.width || "auto",
                                minWidth: col.minWidth || 80,
                              }}>
                              {col.label}
                            </TableCell>
                          ))}
                          {collapseConfig.collapseActions?.onAdd && (
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                width: 80,
                                minWidth: 80,
                                display: !collapseConfig.collapseActions
                                  .showOnAdd
                                  ? "none"
                                  : "table-cell",
                              }}></TableCell>
                          )}
                          {collapseConfig.collapseActions?.onDelete && (
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                width: 80,
                                minWidth: 80,
                                display: !collapseConfig.collapseActions
                                  .showOnDelete
                                  ? "none"
                                  : "table-cell",
                              }}></TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subData.map((item, itemIndex) => (
                          <TableRow key={itemIndex}>
                            {subColumns.map((col) => (
                              <Tooltip
                                title={getNestedValue(
                                  item,
                                  col.key.split(".").slice(1).join("."),
                                )}
                                key={col.key}
                                placement="bottom-start">
                                <TableCell
                                  key={col.key}
                                  align={col.align || "left"}
                                  sx={{
                                    width: col.width || "auto",
                                    minWidth: col.minWidth || 80,
                                  }}>
                                  <Typography
                                    variant="subtitle2"
                                    noWrap
                                    sx={{
                                      textOverflow: "ellipsis",
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                    }}>
                                    {col.render
                                      ? col.render(
                                          getNestedValue(
                                            item,
                                            col.key
                                              .split(".")
                                              .slice(1)
                                              .join("."),
                                          ),
                                          item,
                                          row,
                                        )
                                      : col.isMoney
                                        ? maskCurrency(
                                            getNestedValue(
                                              item,
                                              col.key
                                                .split(".")
                                                .slice(1)
                                                .join("."),
                                            ),
                                          )
                                        : col.isInteger
                                          ? maskInteger(
                                              getNestedValue(
                                                item,
                                                col.key
                                                  .split(".")
                                                  .slice(1)
                                                  .join("."),
                                              ),
                                            )
                                          : getNestedValue(
                                              item,
                                              col.key
                                                .split(".")
                                                .slice(1)
                                                .join("."),
                                            )}
                                  </Typography>
                                </TableCell>
                              </Tooltip>
                            ))}
                            {collapseConfig.collapseActions?.onAdd && (
                              <TableCell align="center">
                                {(typeof collapseConfig.collapseActions
                                  .showOnAdd === "function"
                                  ? collapseConfig.collapseActions.showOnAdd(
                                      item,
                                      row,
                                    )
                                  : (collapseConfig.collapseActions.showOnAdd ??
                                    true)) && (
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() =>
                                      collapseConfig.collapseActions.onAdd(
                                        item,
                                        row,
                                      )
                                    }>
                                    <FontAwesomeIcon
                                      icon={
                                        collapseConfig.collapseActions
                                          .addIcon || "plus"
                                      }
                                    />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                            {collapseConfig.collapseActions?.onDelete && (
                              <TableCell align="center">
                                {(typeof collapseConfig.collapseActions
                                  .showOnDelete === "function"
                                  ? collapseConfig.collapseActions.showOnDelete(
                                      item,
                                      row,
                                    )
                                  : (collapseConfig.collapseActions
                                      .showOnDelete ?? true)) && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      collapseConfig.collapseActions.onDelete(
                                        item,
                                        row,
                                      )
                                    }>
                                    <FontAwesomeIcon
                                      icon={
                                        collapseConfig.collapseActions
                                          .deleteIcon || "trash"
                                      }
                                    />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {index < collapseConfig.key.length - 1 && (
                      <Box sx={{ marginBottom: 2 }} />
                    )}
                  </div>
                );
              })}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  collapseConfig: PropTypes.shape({
    key: PropTypes.arrayOf(PropTypes.string).isRequired,
    titles: PropTypes.arrayOf(PropTypes.string),
    totalField: PropTypes.arrayOf(PropTypes.string),
    onAdd: PropTypes.func,
    onDelete: PropTypes.func,
    addIcon: PropTypes.any,
    deleteIcon: PropTypes.any,
    showOnAdd: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    showOnDelete: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    columns: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          align: PropTypes.oneOf(["left", "right", "center"]),
          render: PropTypes.func,
        }),
      ),
    ).isRequired,
  }).isRequired,
  actions: PropTypes.array,
  showActions: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  rowColorConfig: PropTypes.shape({
    color: PropTypes.string,
    bgColor: PropTypes.string,
  }),
};

export default function CollapsibleTable({
  data,
  columns,
  collapseConfig,
  actions,
  totalPages,
  onPageChange,
  currentPage,
  rowsPerPage,
  onRowsPerPageChange,
  showActions = true,
  defaultOpen,
  quantitySpaces = 1,
  rowColorConfig,
  isLoading = false,
  isFetching = false,
}) {
  const arraySpaces = Array.from({ length: quantitySpaces }, (_, i) => i);

  const totalGeral = {};
  if (collapseConfig.totalField) {
    collapseConfig.totalField.forEach((path) => {
      const [arrayKey, fieldKey] = path.split(".");
      totalGeral[path] = data.reduce((acc, row) => {
        const rowData = row[arrayKey] || [];
        return (
          acc +
          rowData.reduce(
            (subAcc, item) => subAcc + (Number(item[fieldKey]) || 0),
            0,
          )
        );
      }, 0);
    });
  }

  const { selectedOrgan } = useOrgan();
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM;

  // Usa apenas os sufixos dos campos para o mapeamento
  const totalFields = collapseConfig.totalField || [];

  // Filtra as colunas principais que não são de total
  const nonTotalFieldColumns = columns.filter(
    (col) => !totalFields.some((path) => path.endsWith(col.key)),
  );

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      {isLoading || isFetching ? (
        <TableSkeleton
          rows={rowsPerPage > 10 ? 10 : rowsPerPage}
          columns={columns.length}
          showActions={showActions}
        />
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", mt: 2, overflowX: "auto" }}>
            <Table sx={{ minWidth: 1000, width: "100%" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: primaryColor }}>
                  <TableCell />
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      sx={{
                        fontWeight: "bold",
                        color: "#FFFFFF",
                        width: col.width || "auto",
                        minWidth: col.minWidth || 100,
                        maxWidth: col.maxWidth || 200,
                        fontSize: "0.875rem",
                      }}>
                      {col.label}
                    </TableCell>
                  ))}
                  {showActions && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        color: "#FFFFFF",
                        fontSize: "0.875rem",
                      }}>
                      Ações
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, idx) => (
                  <Row
                    key={idx}
                    row={row}
                    columns={columns}
                    collapseConfig={collapseConfig}
                    actions={actions}
                    showActions={showActions}
                    defaultOpen={defaultOpen}
                    rowColorConfig={rowColorConfig}
                  />
                ))}
                {totalFields.length > 0 && (
                  <TableRow>
                    {arraySpaces.map((space) => (
                      <TableCell key={space} />
                    ))}
                    <TableCell
                      colSpan={nonTotalFieldColumns.length}
                      align="right"
                      sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                      Total
                    </TableCell>
                    {totalFields.map((fieldPath) => {
                      const col = columns.find((c) =>
                        fieldPath.endsWith(c.key),
                      );
                      const totalValue = totalGeral[fieldPath];
                      return (
                        <TableCell
                          key={fieldPath}
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            textTransform: "uppercase",
                          }}>
                          {col?.isMoney
                            ? maskCurrency(totalValue)
                            : col?.isInteger
                              ? maskInteger(totalValue)
                              : totalValue}
                        </TableCell>
                      );
                    })}
                    {showActions && <TableCell />}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 2,
              borderTop: "1px solid rgba(224, 224, 224, 1)",
            }}>
            {/* Seletor de itens por página (esquerda) */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">Itens por página:</Typography>
              <FormControl size="small">
                <Select
                  value={rowsPerPage}
                  onChange={(event) => onRowsPerPageChange(event)}
                  sx={{ minWidth: 70 }}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Paginação numérica (centro) */}
            <Pagination
              count={totalPages}
              page={currentPage + 1}
              onChange={(event, page) => onPageChange(page - 1)}
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
            />

            {/* Info de registros (direita) - OPCIONAL */}
            <Typography variant="body2" color="text.secondary">
              {currentPage * rowsPerPage + 1}–
              {Math.min(
                (currentPage + 1) * rowsPerPage,
                totalPages * rowsPerPage,
              )}{" "}
              de {totalPages * rowsPerPage}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
}

CollapsibleTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.oneOf(["left", "right", "center"]),
      render: PropTypes.func,
    }),
  ).isRequired,
  collapseConfig: PropTypes.shape({
    key: PropTypes.arrayOf(PropTypes.string).isRequired,
    titles: PropTypes.arrayOf(PropTypes.string),
    totalField: PropTypes.arrayOf(PropTypes.string),
    columns: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          align: PropTypes.oneOf(["left", "right", "center"]),
          render: PropTypes.func,
        }),
      ),
    ).isRequired,
  }).isRequired,
  actions: PropTypes.array,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  currentPage: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onRowsPerPageChange: PropTypes.func,
  showActions: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  quantitySpaces: PropTypes.number,
  rowColorConfig: PropTypes.shape({
    colorMap: PropTypes.object,
    propertyKey: PropTypes.string,
  }),
};
